import { PayloadAction } from "@reduxjs/toolkit";
import { goBack, push } from "connected-react-router";
import _ from "lodash";
import { call, put, select, takeLatest } from "redux-saga/effects";
import * as Api from "../../apis";
import { setError } from "../Error/slice";
import { RootState } from "../store";
import { openToast } from "../Toast/slice";
import { logout, USER } from "../User/slice";
import {
  clearLoading,
  deleteMembers,
  invite,
  loadMemberPaging,
  loadMembers,
  loadUserPermissions,
  MEMBER,
  resetMember,
  successInvite,
  successLoadMemberPaging,
  successLoadMembers,
  successLoadUserPermissions,
  updateUserPermissions,
} from "./slice";
import {
  IMember,
  RESULT_CODE,
  IUserLoginInfo,
  IMemberList,
  ListPaging,
  RoleType,
  ROWS_PER_PAGE,
  IPermissions,
} from "@thingsw/pitta-modules";

function* handleInviteMember({ payload }: PayloadAction<IMember>) {
  try {
    const res = yield call(Api.inviteMember, payload);
    const { resultcode, message } = res.data as {
      resultcode: RESULT_CODE;
      message: string;
    };
    if (resultcode === "BC_ERR_OK") {
      yield put(successInvite());
      yield put(openToast({ message: "Invite sent" }));
      yield put(goBack());
    } else if (
      (resultcode === "BC_ERR_DUPLICATED" &&
        message === "email is already exist") ||
      (resultcode === "BC_ERR_UNAVAILABLE" &&
        message === "Already have subscription")
    ) {
      yield put(setError("An account is_"));
      yield put(clearLoading());
    } else {
      yield put(setError("Internal server error_"));
      yield put(clearLoading());
    }
  } catch (err) {}
}

function* handleLoadMember() {
  try {
    const { email, tokenType } = yield select(
      (state: RootState) => state[USER]
    );
    const loginInfo = (yield select(
      (state: RootState) => state[USER].loginInfo
    )) as IUserLoginInfo;
    const res = yield call(
      Api.getMemberList,
      email,
      loginInfo.user_token,
      tokenType
    );
    const { resultcode, response } = res.data as {
      resultcode: RESULT_CODE;
      message: string;
      response: IMemberList;
    };
    if (resultcode === "BC_ERR_OK") {
      yield put(successLoadMembers(response));
    } else if (resultcode === "BC_ERR_AUTHENTICATION") {
      yield put(logout());
    } else if (resultcode === "BC_ERR_INVALID_PARAMETER") {
      yield put(setError("Invalid parameter"));
      yield put(clearLoading());
    }
  } catch (err) {
    yield put(setError(err.message));
    yield put(clearLoading());
  }
}

function* handleLoadMemberPaging({ payload }: PayloadAction<ListPaging>) {
  try {
    const { email, tokenType } = yield select(
      (state: RootState) => state[USER]
    );
    const loginInfo = (yield select(
      (state: RootState) => state[USER].loginInfo
    )) as IUserLoginInfo;
    const res = yield call(
      Api.getMemberList,
      email,
      loginInfo.user_token,
      tokenType,
      payload
    );
    const { resultcode, response } = res.data as {
      resultcode: RESULT_CODE;
      message: string;
      response: IMemberList;
    };
    if (response) {
      if (resultcode === "BC_ERR_OK") {
        yield put(successLoadMemberPaging(response));
      } else if (resultcode === "BC_ERR_AUTHENTICATION") {
        yield put(logout());
      } else if (resultcode === "BC_ERR_INVALID_PARAMETER") {
        yield put(setError("Invalid parameter"));
        yield put(clearLoading());
      }
    } else {
      yield put(resetMember());
    }
  } catch (err) {
    yield put(setError(err.message));
    yield put(clearLoading());
  }
}

function* handleDeleteMember({
  payload,
}: PayloadAction<{ deleteEmail: string; deleteEmailUserType: RoleType }[]>) {
  try {
    const { currentPage, memberList } = yield select(
      (state: RootState) => state[MEMBER]
    );
    const email = yield select((state: RootState) => state[USER].email);
    const { user_token } = (yield select(
      (state: RootState) => state[USER].loginInfo
    )) as IUserLoginInfo;
    let success = false;
    for (const user of payload) {
      const res = yield call(
        Api.removeMember,
        user.deleteEmail,
        user.deleteEmailUserType,
        email,
        user_token
      );
      const { resultcode } = res.data as {
        resultcode: RESULT_CODE;
        message: string;
      };
      if (resultcode === "BC_ERR_OK") {
        if (payload.length > 1) {
          yield put(openToast({ message: "Members deleted" }));
          // success = true;
        } else if (payload.length === 1) {
          if (user.deleteEmailUserType === "SubMaster") {
            yield put(openToast({ message: "Admin deleted" }));
          } else {
            yield put(openToast({ message: "Driver deleted" }));
          }
        }
        yield put(clearLoading());
        yield put(loadMemberPaging(currentPage));
        success = true;
      }
    }
    if (success) {
      const cnt = memberList?.inviteMemberCount ?? 1;
      let request = currentPage ?? {
        startIndex: 1,
        endIndex: ROWS_PER_PAGE,
        ordering: 0,
      };
      // mantis - 8540, 51명인 경우 마지막 페이지에 남은 멤버 1명삭제 시 빈 리스트 출력되는 부분 수정
      if (cnt !== 1 && Math.abs(cnt - 1 - request.endIndex) === ROWS_PER_PAGE) {
        request = {
          startIndex: _.floor(request.startIndex / ROWS_PER_PAGE),
          endIndex: _.floor((cnt - 1) / ROWS_PER_PAGE) * ROWS_PER_PAGE,
          ordering: currentPage.ordering,
        };
      } else if (!(request.startIndex <= cnt && cnt <= request.endIndex)) {
        request = {
          // mantis - 8540, 초대멤버가 50명 이상일 때 member삭제 시 빈리스트 출력이슈 수정
          startIndex: _.floor(request.startIndex / ROWS_PER_PAGE) + 1,
          endIndex: _.floor((cnt - 1) / ROWS_PER_PAGE) * ROWS_PER_PAGE,
          // startIndex: request.startIndex - ROWS_PER_PAGE,
          // endIndex: request.endIndex - ROWS_PER_PAGE,
          ordering: currentPage.ordering,
        };
      }

      yield put(loadMemberPaging(request));
      // yield put(successDeleteMembers());
      yield put(push("/members"));
    }
  } catch (err) {}
}

function* handleLoadUserPermissions({ payload }: PayloadAction<string>) {
  try {
    console.log("handleLoadUserPermissions");
    const email = yield select((state: RootState) => state[USER].email);
    const { user_token } = (yield select(
      (state: RootState) => state[USER].loginInfo
    )) as IUserLoginInfo;
    const resp = yield call(Api.getUserPermissions, email, user_token, payload);

    const { resultcode, response } = resp.data as {
      resultcode: RESULT_CODE;
      response: { permissions: IPermissions };
    };
    if (resultcode === "BC_ERR_OK") {
      yield put(successLoadUserPermissions(response.permissions));
    }
  } catch (err) {
    console.error(err);
    yield put(setError(err.message));
    yield put(clearLoading());
  }
}

function* handleUpdateUserPermissions({
  payload,
}: PayloadAction<{ roleSetEmail: string; permissions: IPermissions }>) {
  try {
    const email = yield select((state: RootState) => state[USER].email);
    const { user_token } = (yield select(
      (state: RootState) => state[USER].loginInfo
    )) as IUserLoginInfo;
    const resp = yield call(
      Api.updateUserPermissions,
      email,
      user_token,
      payload.roleSetEmail,
      payload.permissions
    );
    const { resultcode } = resp.data as {
      resultcode: RESULT_CODE;
    };
    if (resultcode === "BC_ERR_OK") {
      yield put(openToast({ message: "Permissions updated" }));
      yield put(successLoadUserPermissions(payload.permissions));
    }
  } catch (err) {
    console.error(err);
    yield put(setError(err.message));
    yield put(clearLoading());
  }
}

export function* watchMember() {
  yield takeLatest(invite, handleInviteMember);
  yield takeLatest(loadMembers, handleLoadMember);
  yield takeLatest(loadMemberPaging, handleLoadMemberPaging);
  yield takeLatest(deleteMembers, handleDeleteMember);
  yield takeLatest(loadUserPermissions, handleLoadUserPermissions);
  yield takeLatest(updateUserPermissions, handleUpdateUserPermissions);
}
